import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";

function VioletSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
        checked ? "bg-violet-600" : "bg-slate-300"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform translate-y-1 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function Field({ label, children, helper, span, required }) {
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {children}
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}

const inputClasses =
  "w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 " +
  "focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition " +
  "disabled:opacity-60 disabled:cursor-not-allowed";

function TextInput({ value, onChange, placeholder, type = "text", disabled, mono }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete="off"
      className={`${inputClasses} ${mono ? "font-mono" : ""}`}
    />
  );
}

function TextArea({ value, onChange, placeholder, disabled, mono, rows = 3 }) {
  return (
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      className={`${inputClasses} resize-y min-h-[88px] ${mono ? "font-mono" : ""}`}
    />
  );
}

function StringSelect({ value, onChange, options, disabled }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={inputClasses}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Select({ value, onChange, options, placeholder, allowNone = false, disabled }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      disabled={disabled}
      className={inputClasses}
    >
      {allowNone && <option value="">{placeholder ?? "— nenhum —"}</option>}
      {!allowNone && value == null && <option value="">{placeholder ?? "— selecione —"}</option>}
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

const DEFAULT_DATABASE_DISCOVERY = {
  enabled: false,
  sourceType: "SqlQuery",
  sqlConnectionString: "",
  sqlQuery: "",
  httpUrl: "",
  httpMethod: "GET",
  httpHeadersJson: "",
  connectionStringTemplate: "",
  databaseNameField: "databaseName",
  connectionStringField: "connectionString",
  maxDatabases: 50,
  migrationParallelism: 4,
  failFast: true,
  commandTimeoutSeconds: 30,
};

function snapshotDatabaseDiscovery(raw) {
  if (!raw || raw.enabled !== true) {
    return { ...DEFAULT_DATABASE_DISCOVERY };
  }

  return {
    ...DEFAULT_DATABASE_DISCOVERY,
    enabled: true,
    sourceType: raw.sourceType || "SqlQuery",
    sqlConnectionString: raw.sqlConnectionString || "",
    sqlQuery: raw.sqlQuery || "",
    httpUrl: raw.httpUrl || "",
    httpMethod: raw.httpMethod || "GET",
    httpHeadersJson: raw.httpHeadersJson || "",
    connectionStringTemplate: raw.connectionStringTemplate || "",
    databaseNameField: raw.databaseNameField || "databaseName",
    connectionStringField: raw.connectionStringField || "connectionString",
    maxDatabases: raw.maxDatabases ?? 50,
    migrationParallelism: raw.migrationParallelism ?? 4,
    failFast: raw.failFast !== false,
    commandTimeoutSeconds: raw.commandTimeoutSeconds ?? 30,
  };
}

function snapshotLinkedTargets(raw) {
  return (raw || [])
    .map((item, index) => ({
      deployTargetId: item.deployTargetId,
      copyFiles: item.copyFiles !== false,
      applyMigrations: item.applyMigrations !== false,
      stopBeforeDeploy: item.stopBeforeDeploy !== false,
      startAfterDeploy: item.startAfterDeploy !== false,
      executionOrder: item.executionOrder ?? index + 1,
      isActive: item.isActive !== false,
    }))
    .filter((item) => item.deployTargetId != null)
    .sort((a, b) => (a.executionOrder || 0) - (b.executionOrder || 0));
}

function ToggleCard({ title, description, checked, onChange, disabled }) {
  return (
    <label
      className={`flex items-start justify-between gap-3 p-3 rounded-xl ring-1 transition cursor-pointer ${
        checked ? "bg-violet-50/60 ring-violet-200" : "bg-slate-50/50 ring-slate-200"
      }`}
    >
      <div className="min-w-0">
        <p className="text-xs font-bold text-slate-800">{title}</p>
        {description && <p className="text-[11px] text-slate-500 mt-0.5">{description}</p>}
      </div>
      <VioletSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </label>
  );
}

function ReadOnlyRow({ label, value, mono, breakAll }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
      <p className={`text-xs text-slate-700 ${mono ? "font-mono" : "font-medium"} ${breakAll ? "break-all whitespace-normal" : "truncate"}`}>
        {value || "—"}
      </p>
    </div>
  );
}

function snapshotFromTarget(target) {
  return {
    displayName: target.displayName ?? "",
    isProduction: !!target.isProduction,
    isActive: target.isActive !== false,
    deploymentMachineId: target.deploymentMachineId ?? null,
    repositoryLocalPath: target.repositoryLocalPath ?? "",
    gitConfigurationId: target.gitConfigurationId ?? null,
    buildConfigurationId: target.buildConfigurationId ?? null,
    databaseRestoreConfigurationId: target.databaseRestoreConfigurationId ?? null,
    databaseDiscovery: snapshotDatabaseDiscovery(target.databaseDiscovery),
    linkedTargets: snapshotLinkedTargets(target.linkedTargets),
    enableCodebaseBackup: !!target.enableCodebaseBackup,
    codebaseBackupS3ConfigurationId: target.codebaseBackupS3ConfigurationId ?? null,
    notificationRecipientUserIds: (target.notificationRecipients || [])
      .map((r) => r.userId)
      .filter((id) => id != null)
      .sort((a, b) => a - b),
  };
}

function isUserNotifiable(user) {
  if (!user) return false;
  if (user.isActive === false) return false;
  const hasWhatsappPhone = !!user.whatsappPhoneNumber || !!user.hasWhatsappPhone;
  return (
    !!user.notifyEmailOnDeploymentCompletion ||
    (!!user.notifyTelegramOnDeploymentCompletion && !!user.telegramChatId) ||
    (!!user.notifyWhatsappOnDeploymentCompletion && hasWhatsappPhone)
  );
}

function formatarDataHora(data) {
  if (!data) return "—";
  return new Date(data).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

export default function TargetSettingsModal({
  target,
  machines,
  gitConfigs,
  buildConfigs,
  restoreConfigs,
  s3Configs,
  targets,
  users,
  onClose,
  onSaved,
}) {
  const initial = useMemo(() => snapshotFromTarget(target), [target]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateDatabaseDiscovery = (changes) =>
    setForm((prev) => ({
      ...prev,
      databaseDiscovery: {
        ...prev.databaseDiscovery,
        ...changes,
      },
    }));
  const updateLinkedTargets = (next) =>
    update(
      "linkedTargets",
      next
        .map((item, index) => ({
          ...item,
          executionOrder: item.executionOrder ?? index + 1,
        }))
        .sort((a, b) => (a.executionOrder || 0) - (b.executionOrder || 0))
    );

  const handleClose = async () => {
    if (isDirty && !saving) {
      const result = await AlertComponent(
        "Alterações não salvas",
        "As informações deste ambiente foram alteradas. Deseja salvar ou descartar as alterações?",
        {
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Salvar",
          cancelButtonText: "Descartar",
          confirmButtonColor: "#7c3aed",
          cancelButtonColor: "#64748b",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCloseButton: false,
          reverseButtons: true,
        },
        null,
        false
      );
      if (result?.isConfirmed) {
        const ok = await handleSave(true);
        if (!ok) return;
      } else if (!result?.dismiss) {
        return;
      }
    }
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  const handleSave = async (silent = false) => {
    if (!form.displayName.trim()) {
      AlertComponent("Atenção", "Informe o nome do ambiente.", { icon: "warning" });
      return false;
    }
    if (!form.deploymentMachineId) {
      AlertComponent("Atenção", "Selecione a máquina deste ambiente.", { icon: "warning" });
      return false;
    }
    if (!form.repositoryLocalPath.trim()) {
      AlertComponent("Atenção", "Informe o caminho local do repositório.", { icon: "warning" });
      return false;
    }
    if (!form.gitConfigurationId) {
      AlertComponent("Atenção", "Selecione a configuração de Git.", { icon: "warning" });
      return false;
    }
    if (!form.buildConfigurationId) {
      AlertComponent("Atenção", "Selecione a configuração de Build.", { icon: "warning" });
      return false;
    }
    if (form.enableCodebaseBackup && !form.codebaseBackupS3ConfigurationId) {
      AlertComponent("Atenção", "Selecione a configuração S3 para o backup de código.", { icon: "warning" });
      return false;
    }

    if (form.linkedTargets.length > 0 && form.databaseDiscovery.enabled) {
      AlertComponent("Atencao", "Nao combine descoberta dinamica de bancos com sites vinculados no mesmo ambiente.", { icon: "warning" });
      return false;
    }

    if (form.databaseDiscovery.enabled) {
      if (form.databaseDiscovery.sourceType === "SqlQuery" && !form.databaseDiscovery.sqlQuery.trim()) {
        AlertComponent("Atencao", "Informe a consulta SQL para descoberta dos bancos.", { icon: "warning" });
        return false;
      }
      if (form.databaseDiscovery.sourceType === "HttpEndpoint" && !form.databaseDiscovery.httpUrl.trim()) {
        AlertComponent("Atencao", "Informe a URL da API para descoberta dos bancos.", { icon: "warning" });
        return false;
      }
      const maxDatabases = Number(form.databaseDiscovery.maxDatabases);
      if (!Number.isFinite(maxDatabases) || maxDatabases < 1 || maxDatabases > 500) {
        AlertComponent("Atencao", "O limite de bancos deve ficar entre 1 e 500.", { icon: "warning" });
        return false;
      }
      const migrationParallelism = Number(form.databaseDiscovery.migrationParallelism);
      if (!Number.isFinite(migrationParallelism) || migrationParallelism < 1 || migrationParallelism > 32) {
        AlertComponent("Atencao", "O limite de migrations simultaneas deve ficar entre 1 e 32.", { icon: "warning" });
        return false;
      }
      const timeout = Number(form.databaseDiscovery.commandTimeoutSeconds);
      if (!Number.isFinite(timeout) || timeout < 1 || timeout > 600) {
        AlertComponent("Atencao", "O timeout da consulta deve ficar entre 1 e 600 segundos.", { icon: "warning" });
        return false;
      }
    }

    setSaving(true);
    try {
      const payload = {
        displayName: form.displayName.trim(),
        isProduction: form.isProduction,
        isActive: form.isActive,
        deploymentMachineId: form.deploymentMachineId,
        repositoryLocalPath: form.repositoryLocalPath.trim(),
        gitConfigurationId: form.gitConfigurationId,
        buildConfigurationId: form.buildConfigurationId,
        databaseRestoreConfigurationId: form.databaseRestoreConfigurationId || null,
        enableCodebaseBackup: form.enableCodebaseBackup,
        codebaseBackupS3ConfigurationId: form.enableCodebaseBackup
          ? form.codebaseBackupS3ConfigurationId
          : null,
        databaseDiscovery: {
          ...form.databaseDiscovery,
          maxDatabases: Number(form.databaseDiscovery.maxDatabases) || 50,
          migrationParallelism: Number(form.databaseDiscovery.migrationParallelism) || 4,
          commandTimeoutSeconds: Number(form.databaseDiscovery.commandTimeoutSeconds) || 30,
        },
        linkedTargets: form.linkedTargets,
        notificationRecipientUserIds: form.notificationRecipientUserIds,
      };
      await axios.put(`${process.env.REACT_APP_API}/admin/deploy-targets/${target.id}`, payload);

      const machine = machines.find((m) => m.id === form.deploymentMachineId);
      const git = gitConfigs.find((g) => g.id === form.gitConfigurationId);
      const build = buildConfigs.find((b) => b.id === form.buildConfigurationId);
      const restore = restoreConfigs.find((r) => r.id === form.databaseRestoreConfigurationId);
      const s3 = s3Configs.find((s) => s.id === form.codebaseBackupS3ConfigurationId);
      const updatedLinkedTargets = form.linkedTargets.map((link) => {
        const linkedTarget = (targets || []).find((item) => item.id === link.deployTargetId);
        return {
          ...link,
          displayName: linkedTarget?.displayName ?? linkedTarget?.siteName ?? `Ambiente #${link.deployTargetId}`,
          deploymentMachineId: linkedTarget?.deploymentMachineId ?? form.deploymentMachineId,
          serverName: linkedTarget?.serverName ?? "",
          siteName: linkedTarget?.siteName ?? "",
          applicationPath: linkedTarget?.applicationPath ?? "/",
          physicalPath: linkedTarget?.physicalPath ?? "",
          isProduction: !!linkedTarget?.isProduction,
          targetIsActive: linkedTarget?.isActive !== false,
        };
      });

      const updatedRecipients = form.notificationRecipientUserIds
        .map((uid) => {
          const u = (users || []).find((x) => x.id === uid);
          if (!u) {
            const existing = (target.notificationRecipients || []).find((r) => r.userId === uid);
            return existing || { userId: uid };
          }
          return {
            userId: u.id,
            email: u.email,
            displayName: u.displayName,
            notifyEmailOnDeploymentCompletion: !!u.notifyEmailOnDeploymentCompletion,
            notifyTelegramOnDeploymentCompletion: !!u.notifyTelegramOnDeploymentCompletion,
            hasTelegramChat: !!u.telegramChatId,
            notifyWhatsappOnDeploymentCompletion: !!u.notifyWhatsappOnDeploymentCompletion,
            hasWhatsappPhone: !!u.whatsappPhoneNumber,
          };
        });

      onSaved({
        ...target,
        displayName: payload.displayName,
        isProduction: form.isProduction,
        isActive: form.isActive,
        deploymentMachineId: form.deploymentMachineId,
        machineName: machine?.name ?? target.machineName,
        repositoryLocalPath: payload.repositoryLocalPath,
        gitConfigurationId: form.gitConfigurationId,
        gitConfigurationName: git?.name ?? target.gitConfigurationName,
        buildConfigurationId: form.buildConfigurationId,
        buildConfigurationName: build?.name ?? target.buildConfigurationName,
        databaseRestoreConfigurationId: form.databaseRestoreConfigurationId || null,
        databaseRestoreConfigurationName: restore?.name ?? null,
        enableCodebaseBackup: form.enableCodebaseBackup,
        codebaseBackupS3ConfigurationId: form.enableCodebaseBackup
          ? form.codebaseBackupS3ConfigurationId
          : null,
        codebaseBackupS3ConfigurationName: form.enableCodebaseBackup ? s3?.name ?? null : null,
        databaseDiscovery: payload.databaseDiscovery.enabled ? payload.databaseDiscovery : null,
        linkedTargets: updatedLinkedTargets,
        notificationRecipients: updatedRecipients,
      });
      if (!silent) AlertComponent("Sucesso", "Ambiente atualizado com sucesso", { icon: "success" });
      return true;
    } catch (err) {
      const mensagem = err?.response?.data?.message || err?.message || "Erro ao salvar ambiente";
      AlertComponent("Erro", mensagem, { icon: "error" });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const activeOptions = (list) =>
    (list || []).map((x) => ({ id: x.id, label: `${x.name}${x.isActive === false ? " (inativo)" : ""}` }));

  const linkedTargetCandidates = (targets || [])
    .filter((item) => item.id !== target.id)
    .filter((item) => item.isActive !== false)
    .filter((item) => !form.deploymentMachineId || item.deploymentMachineId === form.deploymentMachineId)
    .sort((a, b) => (a.displayName || a.siteName || "").localeCompare(b.displayName || b.siteName || ""));
  const selectedLinkedTargetIds = new Set(form.linkedTargets.map((item) => item.deployTargetId));

  const toggleLinkedTarget = (deployTargetId) => {
    if (selectedLinkedTargetIds.has(deployTargetId)) {
      updateLinkedTargets(form.linkedTargets.filter((item) => item.deployTargetId !== deployTargetId));
      return;
    }

    updateLinkedTargets([
      ...form.linkedTargets,
      {
        deployTargetId,
        copyFiles: true,
        applyMigrations: true,
        stopBeforeDeploy: true,
        startAfterDeploy: true,
        executionOrder: form.linkedTargets.length + 1,
        isActive: true,
      },
    ]);
  };

  const updateLinkedTarget = (deployTargetId, changes) => {
    updateLinkedTargets(
      form.linkedTargets.map((item) =>
        item.deployTargetId === deployTargetId ? { ...item, ...changes } : item
      )
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200/60 w-full max-w-3xl flex flex-col overflow-hidden"
        style={{ maxHeight: "min(92vh, 900px)" }}
      >
        {/* Header */}
        <div
          className="px-6 py-5 relative overflow-hidden text-white shrink-0"
          style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
        >
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }} />
          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-purple-300 text-[11px] font-semibold uppercase tracking-wider mb-1">Editar ambiente</p>
              <h2 className="text-lg sm:text-xl font-bold truncate">{form.displayName || target.displayName || target.siteName}</h2>
              <p className="text-purple-200 text-xs mt-0.5 font-mono truncate">{target.siteName} · {target.serverName}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isDirty && (
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-orange-400/20 text-orange-200 ring-1 ring-orange-300/30">
                  Não salvo
                </span>
              )}
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                aria-label="Fechar"
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition disabled:opacity-60"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Identificação */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Nome do ambiente" span={2} required>
              <TextInput
                value={form.displayName}
                onChange={(v) => update("displayName", v)}
                placeholder="Ex: Certificados HOM"
                disabled={saving}
              />
            </Field>
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ToggleCard
                title="Ambiente de produção"
                description="Restore de banco fica bloqueado em produção."
                checked={form.isProduction}
                onChange={(v) => update("isProduction", v)}
                disabled={saving}
              />
              <ToggleCard
                title="Ativo"
                description="Desative para ocultar nas listagens de deploy."
                checked={form.isActive}
                onChange={(v) => update("isActive", v)}
                disabled={saving}
              />
            </div>
          </div>

          {/* Máquina */}
          <div className="pt-4 border-t border-slate-100">
            <h3 className="text-xs font-bold uppercase tracking-wider text-violet-700 mb-3">Máquina</h3>
            <Field label="Máquina deste ambiente" required helper="Cadastrada em Administração > Máquinas.">
              <Select
                value={form.deploymentMachineId}
                onChange={(v) => setForm((prev) => ({ ...prev, deploymentMachineId: v, linkedTargets: [] }))}
                options={activeOptions(machines)}
                disabled={saving}
              />
            </Field>
          </div>

          {/* Repositório & Build */}
          <div className="pt-4 border-t border-slate-100 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-violet-700">Repositório & Build</h3>
            <Field
              label="Caminho local do repositório"
              required
              helper="Pasta na máquina de deploy onde o repositório é clonado."
            >
              <TextInput
                mono
                value={form.repositoryLocalPath}
                onChange={(v) => update("repositoryLocalPath", v)}
                placeholder="C:\\inetpub\\wwwroot\\arquivos-deploy\\Projeto"
                disabled={saving}
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Configuração de Git" required helper="Catálogos > Git.">
                <Select
                  value={form.gitConfigurationId}
                  onChange={(v) => update("gitConfigurationId", v)}
                  options={activeOptions(gitConfigs)}
                  disabled={saving}
                />
              </Field>
              <Field label="Configuração de Build" required helper="Catálogos > Build.">
                <Select
                  value={form.buildConfigurationId}
                  onChange={(v) => update("buildConfigurationId", v)}
                  options={activeOptions(buildConfigs)}
                  disabled={saving}
                />
              </Field>
            </div>
          </div>

          {/* Banco de dados */}
          <div className="pt-4 border-t border-slate-100 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-violet-700 mb-3">Banco de dados</h3>
            <Field
              label="Configuração de Restore"
              helper="Deixe em branco se este ambiente não restaura banco. Catálogos > DB Restore."
            >
              <Select
                allowNone
                placeholder="— Sem restore —"
                value={form.databaseRestoreConfigurationId}
                onChange={(v) => update("databaseRestoreConfigurationId", v)}
                options={activeOptions(restoreConfigs)}
                disabled={saving}
              />
            </Field>

            <div className="space-y-4">
              <ToggleCard
                title="Descoberta dinamica de bancos"
                description="Aplica migrations em todos os bancos retornados para este ambiente."
                checked={form.databaseDiscovery.enabled}
                onChange={(v) => updateDatabaseDiscovery({ enabled: v })}
                disabled={saving}
              />

              {form.databaseDiscovery.enabled && (
                <div className="rounded-xl bg-slate-50/60 ring-1 ring-slate-200 p-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field label="Origem da lista de bancos" required>
                      <StringSelect
                        value={form.databaseDiscovery.sourceType}
                        onChange={(v) => updateDatabaseDiscovery({ sourceType: v })}
                        disabled={saving}
                        options={[
                          { value: "SqlQuery", label: "Consulta SQL" },
                          { value: "HttpEndpoint", label: "API HTTP" },
                        ]}
                      />
                    </Field>
                    <Field label="Limite de bancos" required helper="Protecao contra retorno maior que o esperado.">
                      <TextInput
                        type="number"
                        value={form.databaseDiscovery.maxDatabases}
                        onChange={(v) => updateDatabaseDiscovery({ maxDatabases: v })}
                        placeholder="50"
                        disabled={saving}
                      />
                    </Field>
                    <Field label="Migrations simultaneas" required helper="Quantos bancos atualizar ao mesmo tempo.">
                      <TextInput
                        type="number"
                        value={form.databaseDiscovery.migrationParallelism}
                        onChange={(v) => updateDatabaseDiscovery({ migrationParallelism: v })}
                        placeholder="4"
                        disabled={saving}
                      />
                    </Field>
                  </div>

                  {form.databaseDiscovery.sourceType === "SqlQuery" ? (
                    <div className="space-y-4">
                      <Field
                        label="Connection string da consulta"
                        helper="Opcional. Se ficar vazio, usa a mesma connection string do Web.config do ambiente."
                      >
                        <TextInput
                          mono
                          value={form.databaseDiscovery.sqlConnectionString}
                          onChange={(v) => updateDatabaseDiscovery({ sqlConnectionString: v })}
                          placeholder="Server=...;Database=...;User Id=...;"
                          disabled={saving}
                        />
                      </Field>
                      <Field
                        label="Consulta SQL"
                        required
                        helper="Retorne databaseName, connectionString, ou uma unica coluna com o nome do banco."
                      >
                        <TextArea
                          mono
                          rows={5}
                          value={form.databaseDiscovery.sqlQuery}
                          onChange={(v) => updateDatabaseDiscovery({ sqlQuery: v })}
                          placeholder="SELECT NomeBanco AS databaseName FROM ..."
                          disabled={saving}
                        />
                      </Field>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Field label="URL da API" required helper="A resposta pode ser um array ou um objeto com databases/items/data.">
                        <TextInput
                          mono
                          value={form.databaseDiscovery.httpUrl}
                          onChange={(v) => updateDatabaseDiscovery({ httpUrl: v })}
                          placeholder="https://api.exemplo.com/bancos"
                          disabled={saving}
                        />
                      </Field>
                      <Field label="Headers HTTP em JSON" helper='Opcional. Ex: {"X-Api-Key":"valor"}'>
                        <TextArea
                          mono
                          rows={3}
                          value={form.databaseDiscovery.httpHeadersJson}
                          onChange={(v) => updateDatabaseDiscovery({ httpHeadersJson: v })}
                          placeholder='{"X-Api-Key":"valor"}'
                          disabled={saving}
                        />
                      </Field>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Campo do nome do banco" required>
                      <TextInput
                        mono
                        value={form.databaseDiscovery.databaseNameField}
                        onChange={(v) => updateDatabaseDiscovery({ databaseNameField: v })}
                        placeholder="databaseName"
                        disabled={saving}
                      />
                    </Field>
                    <Field label="Campo da connection string" required>
                      <TextInput
                        mono
                        value={form.databaseDiscovery.connectionStringField}
                        onChange={(v) => updateDatabaseDiscovery({ connectionStringField: v })}
                        placeholder="connectionString"
                        disabled={saving}
                      />
                    </Field>
                  </div>

                  <Field
                    label="Template de connection string"
                    helper="Opcional. Use {DatabaseName}; se vazio, o sistema troca o banco na connection string do Web.config."
                  >
                    <TextInput
                      mono
                      value={form.databaseDiscovery.connectionStringTemplate}
                      onChange={(v) => updateDatabaseDiscovery({ connectionStringTemplate: v })}
                      placeholder="Server=...;Database={DatabaseName};User Id=...;"
                      disabled={saving}
                    />
                  </Field>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Timeout da consulta" required helper="Em segundos.">
                      <TextInput
                        type="number"
                        value={form.databaseDiscovery.commandTimeoutSeconds}
                        onChange={(v) => updateDatabaseDiscovery({ commandTimeoutSeconds: v })}
                        placeholder="30"
                        disabled={saving}
                      />
                    </Field>
                    <ToggleCard
                      title="Parar no primeiro erro"
                      description="Quando desligado, tenta os demais bancos e falha no final com o resumo."
                      checked={form.databaseDiscovery.failFast}
                      onChange={(v) => updateDatabaseDiscovery({ failFast: v })}
                      disabled={saving}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sites vinculados */}
          <div className="pt-4 border-t border-slate-100 space-y-3">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-violet-700">Sites vinculados ao deploy</h3>
              <p className="text-[11px] text-slate-500 mt-1">
                Sites da mesma maquina que receberao o mesmo build quando este ambiente for publicado.
              </p>
            </div>
            {form.databaseDiscovery.enabled && (
              <p className="text-xs text-amber-700 px-4 py-3 rounded-xl bg-amber-50 ring-1 ring-amber-200">
                Desative a descoberta dinamica de bancos para configurar sites vinculados neste ambiente.
              </p>
            )}
            {!form.databaseDiscovery.enabled && linkedTargetCandidates.length === 0 && (
              <p className="text-xs text-slate-500 px-4 py-3 rounded-xl bg-slate-50 ring-1 ring-slate-200">
                Nenhum outro site encontrado na mesma maquina.
              </p>
            )}
            {!form.databaseDiscovery.enabled && linkedTargetCandidates.length > 0 && (
              <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200 overflow-hidden">
                {linkedTargetCandidates.map((candidate) => {
                  const selected = selectedLinkedTargetIds.has(candidate.id);
                  const link = form.linkedTargets.find((item) => item.deployTargetId === candidate.id);
                  return (
                    <li key={candidate.id} className={selected ? "bg-violet-50/40" : "bg-white"}>
                      <label className="flex items-start gap-3 px-4 py-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleLinkedTarget(candidate.id)}
                          disabled={saving}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`text-sm font-semibold truncate ${selected ? "text-violet-900" : "text-slate-800"}`}>
                              {candidate.displayName || candidate.siteName || `Ambiente #${candidate.id}`}
                            </p>
                            {candidate.isProduction && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-rose-100 text-rose-700 ring-1 ring-rose-200">
                                Producao
                              </span>
                            )}
                            {candidate.isActive === false && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-slate-200 text-slate-600 ring-1 ring-slate-300">
                                Inativo
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 truncate">
                            {candidate.siteName} {candidate.applicationPath ? `- ${candidate.applicationPath}` : ""}
                          </p>
                          {selected && link && (
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <label className="flex items-center gap-2 text-xs text-slate-600">
                                <input
                                  type="checkbox"
                                  checked={link.copyFiles}
                                  onChange={(e) => updateLinkedTarget(candidate.id, { copyFiles: e.target.checked })}
                                  disabled={saving}
                                  className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                />
                                Copiar arquivos
                              </label>
                              <label className="flex items-center gap-2 text-xs text-slate-600">
                                <input
                                  type="checkbox"
                                  checked={link.applyMigrations}
                                  onChange={(e) => updateLinkedTarget(candidate.id, { applyMigrations: e.target.checked })}
                                  disabled={saving}
                                  className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                />
                                Aplicar migrations
                              </label>
                              <label className="flex items-center gap-2 text-xs text-slate-600">
                                <input
                                  type="checkbox"
                                  checked={link.stopBeforeDeploy}
                                  onChange={(e) => updateLinkedTarget(candidate.id, { stopBeforeDeploy: e.target.checked })}
                                  disabled={saving}
                                  className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                />
                                Parar antes
                              </label>
                              <label className="flex items-center gap-2 text-xs text-slate-600">
                                <input
                                  type="checkbox"
                                  checked={link.startAfterDeploy}
                                  onChange={(e) => updateLinkedTarget(candidate.id, { startAfterDeploy: e.target.checked })}
                                  disabled={saving}
                                  className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                />
                                Iniciar depois
                              </label>
                            </div>
                          )}
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {form.linkedTargets.length > 0 && (
              <p className="text-[11px] text-slate-500">
                {form.linkedTargets.length} site{form.linkedTargets.length !== 1 ? "s" : ""} vinculado{form.linkedTargets.length !== 1 ? "s" : ""}.
              </p>
            )}
          </div>

          {/* Backup de código */}
          <div className="pt-4 border-t border-slate-100 space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-violet-700">Backup do código-fonte</h3>
            <ToggleCard
              title="Habilitar backup antes do deploy"
              description="Sobe o código atual para um bucket S3 antes de sobrescrever."
              checked={form.enableCodebaseBackup}
              onChange={(v) => update("enableCodebaseBackup", v)}
              disabled={saving}
            />
            {form.enableCodebaseBackup && (
              <Field label="Bucket S3 para o backup" required helper="Catálogos > S3.">
                <Select
                  value={form.codebaseBackupS3ConfigurationId}
                  onChange={(v) => update("codebaseBackupS3ConfigurationId", v)}
                  options={activeOptions(s3Configs)}
                  disabled={saving}
                />
              </Field>
            )}
          </div>

          {/* Destinatários de notificação */}
          <div className="pt-4 border-t border-slate-100 space-y-3">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-violet-700">Destinatários de notificação</h3>
              <p className="text-[11px] text-slate-500 mt-1">
                Usuários extras que receberão notificação ao finalizar deploys neste ambiente. Apenas usuários com e-mail, Telegram ou WhatsApp habilitado são listados.
              </p>
            </div>
            {(() => {
              const elegiveis = (users || []).filter(isUserNotifiable);
              const selectedSet = new Set(form.notificationRecipientUserIds);
              if (elegiveis.length === 0) {
                return (
                  <p className="text-xs text-slate-500 px-4 py-3 rounded-xl bg-slate-50 ring-1 ring-slate-200">
                    Nenhum usuário com e-mail, Telegram ou WhatsApp habilitado para notificação.
                  </p>
                );
              }
              const toggle = (uid) => {
                const next = selectedSet.has(uid)
                  ? form.notificationRecipientUserIds.filter((id) => id !== uid)
                  : [...form.notificationRecipientUserIds, uid];
                update("notificationRecipientUserIds", next.sort((a, b) => a - b));
              };
              return (
                <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200 overflow-hidden">
                  {elegiveis.map((u) => {
                    const checked = selectedSet.has(u.id);
                    return (
                      <li key={u.id}>
                        <label
                          className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                            checked ? "bg-violet-50/60" : "hover:bg-slate-50"
                          } ${saving ? "cursor-not-allowed opacity-70" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(u.id)}
                            disabled={saving}
                            className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`text-sm font-semibold truncate ${checked ? "text-violet-900" : "text-slate-800"}`}>
                                {u.displayName || u.email}
                              </p>
                              {u.notifyTelegramOnDeploymentCompletion && u.telegramChatId && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-sky-100 text-sky-700 ring-1 ring-sky-200">
                                  Telegram
                                </span>
                              )}
                              {u.notifyWhatsappOnDeploymentCompletion && (u.whatsappPhoneNumber || u.hasWhatsappPhone) && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-lime-100 text-lime-700 ring-1 ring-lime-200">
                                  WhatsApp
                                </span>
                              )}
                              {u.notifyEmailOnDeploymentCompletion && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                                  E-mail
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
            {form.notificationRecipientUserIds.length > 0 && (
              <p className="text-[11px] text-slate-500">
                {form.notificationRecipientUserIds.length} destinatário{form.notificationRecipientUserIds.length !== 1 ? "s" : ""} selecionado{form.notificationRecipientUserIds.length !== 1 ? "s" : ""}.
              </p>
            )}
          </div>

          {/* Informações do IIS (readonly) */}
          <div className="pt-4 border-t border-slate-100">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Informações do IIS (somente leitura)</h3>
            <div className="rounded-xl bg-slate-50/60 ring-1 ring-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              <ReadOnlyRow label="Server" value={target.serverName} mono />
              <ReadOnlyRow label="Máquina" value={target.machineName} />
              <ReadOnlyRow label="Site" value={target.siteName} mono />
              <ReadOnlyRow label="Application pool" value={target.applicationPool} mono />
              <div className="sm:col-span-2">
                <ReadOnlyRow label="Application path" value={target.applicationPath} mono breakAll />
              </div>
              <div className="sm:col-span-2">
                <ReadOnlyRow label="Caminho físico" value={target.physicalPath} mono breakAll />
              </div>
              <div className="sm:col-span-2">
                <ReadOnlyRow label="Último scan" value={formatarDataHora(target.lastScannedAt)} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3 shrink-0">
          <p className="text-xs text-slate-500 min-w-0 truncate">
            {isDirty ? "Há alterações pendentes de salvamento." : "Nenhuma alteração pendente."}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 bg-white ring-1 ring-slate-200 hover:bg-slate-50 transition disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => handleSave(false)}
              disabled={!isDirty || saving}
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
              style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Salvando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Salvar alterações
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
